/**
 * Prijs- en checkout-copy — kernlogica in `@comforttool/commerce` (Worker gebruikt dezelfde bundel).
 */

import type { TypeWoning, UserSegment } from '@comforttool/comfortcore';
import {
  BTW_FRACTIE,
  CHECKOUT_ADDONS,
  FLOOR_BAND_MAX_XL_M2,
  PRICING_PARTICULIER_INCL_BTW,
  PRICING_ZAKELIJK_EX_BTW,
  prijsSnapshotVoorInvoer,
  type BundleTier,
  type CheckoutAddonId,
  type PrijsSnapshot,
} from '@comforttool/commerce';

export type { BundleTier, FloorPriceBand, PricedFloorBand, PrijsSnapshot } from '@comforttool/commerce';
export {
  BTW_FRACTIE,
  FLOOR_BAND_MAX_L_M2,
  FLOOR_BAND_MAX_M_M2,
  FLOOR_BAND_MAX_S_M2,
  FLOOR_BAND_MAX_XL_M2,
  PRICING_PARTICULIER_BASIS_INCL_BTW,
  PRICING_PARTICULIER_INCL_BTW,
  PRICING_ZAKELIJK_BASIS_EX_BTW,
  PRICING_ZAKELIJK_EX_BTW,
  bandOmschrijving,
  floorPriceBand,
  isZakelijkePrijzenSegment,
  prijsSnapshotVoorInvoer,
} from '@comforttool/commerce';

export {
  CHECKOUT_ADDON_IDS,
  CHECKOUT_ADDONS,
  type CheckoutAddonDef,
  type CheckoutAddonId,
  isCheckoutAddonId,
  sanitizeCheckoutAddonIds,
} from '@comforttool/commerce';

export const FAIR_USE_MAX_RAPPORT_REGENERATIES = 3;
export const FAIR_USE_MAX_KAMERS_STANDAARD = 15;

export function formatPriceEur(amount: number, maxFractionDigits: 0 | 1 | 2 = 2): string {
  const rounded = maxFractionDigits === 0 ? Math.round(amount) : amount;
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: maxFractionDigits === 0 ? 0 : Number.isInteger(rounded) ? 0 : 2,
    maximumFractionDigits: maxFractionDigits,
  }).format(rounded);
}

/** @deprecated gebruik formatPriceEur voor nieuwe code */
export function formatPriceEurExBtw(amount: number): string {
  return formatPriceEur(amount, 0);
}

export const PRICING_WEIGHTED_AVG_EUR_EX_BTW = Math.round(
  ((PRICING_PARTICULIER_INCL_BTW.S +
    PRICING_PARTICULIER_INCL_BTW.M +
    PRICING_PARTICULIER_INCL_BTW.L +
    PRICING_PARTICULIER_INCL_BTW.XL) /
    4 /
    (1 + BTW_FRACTIE)) *
    100
) / 100;

export const PRICING_PARTICULIER_HINT_EUR_EX_BTW = Math.round(
  (PRICING_PARTICULIER_INCL_BTW.S / (1 + BTW_FRACTIE)) * 100
) / 100;
export const PRICING_B2B_EQUIVALENT_EUR_EX_BTW = PRICING_ZAKELIJK_EX_BTW.M;
export const PRICING_MIX_PARTICULIER_PCT = 75;
export const PRICING_MIX_B2B_PCT = 25;

export type PrijsInvoerContext = {
  user_segment: UserSegment;
  vloeroppervlakte: number;
  type_woning: TypeWoning;
  bundleTier?: BundleTier;
};

export function indicativeBundlePriceLineForInput(input: PrijsInvoerContext): string {
  const s = prijsSnapshotVoorInvoer(input);
  if (s.requiresQuote) {
    return 'Offerte op maat — geen vaste webprijs voor dit oppervlak';
  }
  if (s.isZakelijk) {
    const tier = s.bundleTier === 'basis' ? 'Comfort Basis' : 'Comfort Plus';
    return `${formatPriceEur(s.hoofdbedrag, 0)} ex btw · totaal ${formatPriceEur(s.totaalInclBtw)} incl. btw · ${tier} (${s.bandOmschrijving})`;
  }
  const tier = s.bundleTier === 'basis' ? 'Comfort Basis' : 'Comfort Plus';
  return `${formatPriceEur(s.hoofdbedrag)} incl. btw · ${tier} (${s.bandOmschrijving})`;
}

export function indicativeBundlePriceLine(): string {
  return `Thuis vanaf ${formatPriceEur(PRICING_PARTICULIER_INCL_BTW.S)} incl. btw · zakelijk vanaf ${formatPriceEur(
    PRICING_ZAKELIJK_EX_BTW.S,
    0
  )} ex btw`;
}

export function indicativePricingParagraphForInput(input: PrijsInvoerContext): string {
  const s = prijsSnapshotVoorInvoer(input);
  const kern = indicativeBundlePriceLineForInput(input);
  if (s.requiresQuote) {
    return `${kern}. Neem contact op voor een offerte; online betalen gaat voor deze invoer niet.`;
  }
  if (s.isZakelijk) {
    return `${kern}. Band ${s.band}: ${s.bandOmschrijving}. Btw ${formatPriceEur(s.btwBedrag)} (21%). Het exacte bedrag zie je op de betaalpagina vlak voordat je betaalt.`;
  }
  const exNetto = Math.round((s.hoofdbedrag - s.btwBedrag) * 100) / 100;
  return `${kern}. Band ${s.band}: ${s.bandOmschrijving}. Ter info: netto-equivalent ex btw circa ${formatPriceEur(exNetto)}. Het exacte bedrag zie je op de betaalpagina vlak voordat je betaalt.`;
}

export function indicativePricingParagraph(): string {
  return `Prijzen op m²-band en doelgroep: thuis ${formatPriceEur(PRICING_PARTICULIER_INCL_BTW.S)} / ${formatPriceEur(
    PRICING_PARTICULIER_INCL_BTW.M
  )} / ${formatPriceEur(PRICING_PARTICULIER_INCL_BTW.L)} / ${formatPriceEur(
    PRICING_PARTICULIER_INCL_BTW.XL
  )} incl. btw (S/M/L/XL); zakelijk ${formatPriceEur(PRICING_ZAKELIJK_EX_BTW.S, 0)} / ${formatPriceEur(
    PRICING_ZAKELIJK_EX_BTW.M,
    0
  )} / ${formatPriceEur(PRICING_ZAKELIJK_EX_BTW.L, 0)} / ${formatPriceEur(
    PRICING_ZAKELIJK_EX_BTW.XL,
    0
  )} ex btw. Comfort Basis (lager) en Comfort Plus (uitgebreider leesrapport) kies je bij het bestellen. Boven ${FLOOR_BAND_MAX_XL_M2} m²: offerte. Optioneel: tweede berekening. Het exacte bedrag zie je op de betaalpagina.`;
}

/** Totaal inclusief btw voor weergave bij geselecteerde add-ons (client-side schatting; server is leidend). */
export function geschatTotaalInclBtwMetAddons(
  snapshot: PrijsSnapshot,
  addonIds: readonly string[]
): number | null {
  if (snapshot.requiresQuote) return null;
  let t = snapshot.totaalInclBtw;
  for (const id of addonIds) {
    if (id !== 'tweede_berekening') continue;
    const a = CHECKOUT_ADDONS[id as CheckoutAddonId];
    if (snapshot.isZakelijk) {
      const vat = Math.round(a.eurExBtwB2B * BTW_FRACTIE * 100) / 100;
      t += Math.round((a.eurExBtwB2B + vat) * 100) / 100;
    } else {
      t += a.eurInclBtwB2C;
    }
  }
  return Math.round(t * 100) / 100;
}
